import Bullhorn16 from '@carbon/icons-react/es/bullhorn/16';
import Document16 from '@carbon/icons-react/es/document/16';
import Roadmap16 from '@carbon/icons-react/es/roadmap/16';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { TemplateId } from 'api/models';
import { ReactComponent as FormTemplateIconSvg } from 'assets/svgs/form-instance-notification-config-icon.svg';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { TemplatesList } from 'components/organisms/templates/list';
import { INTERMEDIATE_TEMPLATE_ID } from 'components/organisms/templates/list/list-section';
import { Krypton } from 'components/templates/krypton';
import { RoutePaths } from 'constant';
import { Template, TemplateObjectType } from 'data/templates/api';
import { clearProjectTemplateActiveRecord } from 'data/templates/projects';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import {
  getTemplatesQueryKey,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  useTemplatesQuery,
} from 'queries';
import {
  useDeleteFormTemplateMutation,
  useDuplicateFormTemplateMutation,
  useFormTemplatesListQuery,
} from 'queries/form-template';
import { getTemplateUrl, stripSlashes } from 'utils/urls';

export const TemplateListRoute: FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const docTitle = useTranslation('templates');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const title = useTranslation('templates');
  const { data: templates, isLoading } = useTemplatesQuery({
    onSettled: () => {
      dispatch(clearProjectTemplateActiveRecord());
    },
  });
  const { data: formTemplates, isLoading: formTemplatesLoading } =
    useFormTemplatesListQuery({
      refetchOnMount: 'always',
    });

  const { mutateAsync: deleteFormTemplateMutation } =
    useDeleteFormTemplateMutation();

  const { mutateAsync: deleteTemplateAsync } = useDeleteTemplateMutation();
  const { mutateAsync: duplicateTemplateAsync } =
    useDuplicateTemplateMutation();
  const { mutateAsync: duplicateFormTemplateMutation } =
    useDuplicateFormTemplateMutation();

  const onBtnClick = (id: number, templateType: TemplateObjectType) => {
    history.push(getTemplateUrl(TemplateId(id), templateType));
  };

  const viewTemplate = useTranslation('template.view');
  const showDuplicateSuccessToast = (
    name: string,
    { templateId, templateType },
  ) => {
    const desc = intl.formatMessage(
      {
        id: 'template.duplicated_msg_desc',
      },
      {
        name,
      },
    );

    const service = toastService({
      title: intl.formatMessage({
        id: 'template.duplicated_msg_title',
      }),
      message: (
        <ToastDescription
          buttonText={viewTemplate}
          message={desc}
          onClick={() => onBtnClick(templateId, templateType)}
        />
      ),
    });

    service.success();
  };

  const showDeleteSuccessToast = (name: string) => {
    const service = toastService({
      title: intl.formatMessage({
        id: 'template.deleted_msg_title',
      }),
      message: intl.formatMessage(
        {
          id: 'template.deleted_msg_desc',
        },
        {
          name,
        },
      ),
    });

    service.success();
  };

  const onDelete = async (id, objectType: TemplateObjectType) => {
    if (objectType === TemplateObjectType.Form) {
      if (formTemplates) {
        const template = formTemplates.find((templ) => templ.templateId === id);

        if (template) {
          await deleteFormTemplateMutation(
            { templateId: template.templateId },
            {
              onSuccess: () => {
                showDeleteSuccessToast(template.name);
              },
            },
          );
        }
      }
    } else if (templates) {
      const template = templates.find((templ) => templ.templateId === id);

      await deleteTemplateAsync(id);
      showDeleteSuccessToast(template?.templateName || '');
    }
  };

  const translate = useTranslate();

  const onDuplicate = async (id, objectType: TemplateObjectType) => {
    if (objectType === TemplateObjectType.Form) {
      if (formTemplates) {
        const newTemplate = formTemplates.find(
          (templ) => templ.templateId === id,
        );
        const newName = translate('form_template.duplicate.name', {
          templateName: newTemplate?.name || '',
        });

        if (newTemplate) {
          try {
            const response = await duplicateFormTemplateMutation({
              templateId: newTemplate?.templateId,
            });

            showDuplicateSuccessToast(newName, {
              templateId: response.templateId,
              templateType: TemplateObjectType.Form,
            });
          } catch (e) {
            toastService({
              title: translate('form_template.duplication.failed.title'),
              message: translate('form_template.duplication.failed.message'),
            }).error();
          }
        }
      }
    } else if (templates) {
      const newTemplate = templates?.find((templ) => templ.templateId === id);

      const newName = `Copy of ${newTemplate?.templateName || ''}`;

      if (newTemplate) {
        await queryClient.setQueryData<Template[]>(
          getTemplatesQueryKey(),
          (prev = []) => [
            {
              ...newTemplate,
              templateName: newName,
              templateId: INTERMEDIATE_TEMPLATE_ID,
            },
            ...prev,
          ],
        );
        const response = await duplicateTemplateAsync(id);

        showDuplicateSuccessToast(newName, {
          templateId: response.templateId,
          templateType: newTemplate.objectType,
        });
      }
    }
  };

  if (stripSlashes(location.pathname) === stripSlashes(RoutePaths.templates)) {
    return <Redirect to={RoutePaths.projectTemplateRoot} />;
  }

  const navLinkObj = [
    {
      value: [
        {
          menuName: <FormattedMessage id="templates.projects" />,
          menuIcon: <Roadmap16 />,
          route: RoutePaths.projectTemplateRoot,
        },
        {
          menuName: <FormattedMessage id="templates.documents" />,
          menuIcon: <Document16 />,
          route: RoutePaths.documentTemplateRoot,
        },
        {
          menuName: <FormattedMessage id="templates.status_updates" />,
          menuIcon: <Bullhorn16 />,
          route: RoutePaths.statusUpdateTemplateRoot,
        },
        {
          menuName: <FormattedMessage id="templates.forms" />,
          menuIcon: <FormTemplateIconSvg />,
          route: RoutePaths.formTemplateRoot,
        },
      ],
    },
  ];

  return (
    <Krypton documentTitle={docTitle} title={title} leftMenuItems={navLinkObj}>
      <TemplatesList
        loading={isLoading}
        formTemplatesLoading={formTemplatesLoading}
        templates={templates}
        formTemplates={formTemplates || []}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
      />
    </Krypton>
  );
};
